import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api, errorMessage, unwrap } from "../api/client";
import { keys, useAcceptInvitation, useAuthConfig } from "../api/queries";
import type { Schemas } from "../api/client";
import { usePageTitle } from "../components/ui";

/** Retour sûr : chemin local uniquement (pas de redirection ouverte). */
function safeReturn(value: string | null): string {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : "/";
}

export function LoginPage() {
  usePageTitle("Connexion");
  const config = useAuthConfig();
  const [params] = useSearchParams();
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(params.get("error"));
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const client = useQueryClient();
  const returnUrl = safeReturn(params.get("returnUrl"));
  const [inviting, setInviting] = useState(params.has("invite"));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unwrap(api.POST("/auth/token-login", { body: { token: token.trim() } }));
      await client.invalidateQueries({ queryKey: keys.me });
      navigate(returnUrl, { replace: true });
    } catch (err) {
      setError(errorMessage(err) === "Unauthorized" ? "Credential invalide, expiré ou révoqué." : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center">
      <div className="login">
        <div className="brand">
          <img src="/favicon.svg" alt="" />
          ProjectBrain
        </div>
        <div className="card">
          <h2 style={{ marginBottom: 6 }}>Connexion</h2>
          <p className="muted" style={{ marginTop: 0 }}>Le cerveau partagé de vos projets : tâches, sessions d'agents et connaissance.</p>
          {error && <div className="error" role="alert" style={{ marginBottom: 12 }}>{error}</div>}
          {config.data?.sso && (
            <>
              <a className="button primary" style={{ width: "100%" }} href={`/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`}>
                Continuer avec le SSO
              </a>
              <div className="divider">ou</div>
            </>
          )}
          {inviting ? (
            <InvitationForm onCancel={() => setInviting(false)} onDone={() => navigate("/", { replace: true })} />
          ) : (
          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="token">Credential ProjectBrain</label>
              <input
                id="token"
                type="password"
                autoComplete="off"
                placeholder="pb_…"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
              <p className="small faint" style={{ margin: "6px 0 0" }}>
                Jeton personnel ou credential d'appareil (<code>brain login</code>). Il n'est pas conservé par le navigateur : il ouvre une session.
              </p>
            </div>
            <button className={config.data?.sso ? "" : "primary"} style={{ width: "100%" }} disabled={busy || !token.trim().startsWith("pb_")}>
              {busy ? "Connexion…" : "Se connecter"}
            </button>
            <button type="button" className="ghost small" style={{ width: "100%", marginTop: 8 }} onClick={() => setInviting(true)}>
              J'ai une invitation
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Invitation (PB-53) : le code crée le compte et ouvre la session. Les deux credentials ne sont montrés qu'une fois, pour
 * brancher la CLI et les agents de ce poste.
 */
function InvitationForm({ onCancel, onDone }: { onCancel: () => void; onDone: () => void }) {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("invite") ?? "");
  const [name, setName] = useState("");
  const accept = useAcceptInvitation();
  const [accepted, setAccepted] = useState<Schemas["InvitationAcceptedDto"] | null>(null);

  if (accepted)
    return (
      <div className="stack" style={{ gap: 8 }} aria-label="Invitation acceptée">
        <p style={{ margin: 0 }}>
          Bienvenue {accepted.user} : vous êtes {accepted.role} du projet <strong>{accepted.project}</strong>.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          Pour la CLI et vos agents, dans un clone du dépôt : lancez <code>brain login --token-stdin</code> et collez votre credential
          humain (il ne sera plus affiché). La CLI en tire un credential distinct pour vos agents.
        </p>
        <pre className="small" data-testid="cli-login" style={{ userSelect: "all", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
          {accepted.human_token}
        </pre>
        <button className="primary" onClick={onDone}>
          Continuer
        </button>
      </div>
    );

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        accept.mutate({ code: code.trim(), display_name: name.trim() }, { onSuccess: setAccepted });
      }}
    >
      {accept.isError && <div className="error" role="alert" style={{ marginBottom: 12 }}>{errorMessage(accept.error)}</div>}
      <div className="field">
        <label htmlFor="invite-code">Code d'invitation</label>
        <input id="invite-code" autoComplete="off" placeholder="pbi_…" value={code} onChange={(e) => setCode(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="invite-name">Votre nom dans l'équipe</label>
        <input id="invite-name" autoComplete="name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <button className="primary" style={{ width: "100%" }} disabled={accept.isPending || !code.trim().startsWith("pbi_") || !name.trim()}>
        {accept.isPending ? "Acceptation…" : "Rejoindre le projet"}
      </button>
      <button type="button" className="ghost small" style={{ width: "100%", marginTop: 8 }} onClick={onCancel}>
        J'ai déjà un credential
      </button>
    </form>
  );
}
